/**
 * The four asset slots (P13) — logo, dark logo, favicon, login background.
 *
 * Busy and error state is **per slot**: uploads to different slots are
 * different gestures, and a favicon that failed must not paint the logo
 * upload red. The error rendered for an `UNSUPPORTED_MEDIA_TYPE` is the
 * standard `ErrorState` copy for that code ("Choose a PNG, JPEG, WebP, ICO or
 * SVG image") — the server sniffed the bytes, not just the extension, and the
 * copy says what to do rather than what was detected.
 *
 * Previews come from the payload's own URLs (`/branding/assets/ast_…`): the
 * server serves what it stored, so the preview cannot show a file that failed
 * sanitisation.
 */

import type { BrandingPayload } from '@rd/shared';
import { useState, type ReactElement } from 'react';

import { ErrorState } from '../../components/ErrorState.js';
import { Field } from '../../components/Field.js';

import { useUploadAsset, type AssetKind } from './useBrandingAdmin.js';

interface Slot {
  kind: AssetKind;
  label: string;
  hint: string;
  url(payload: BrandingPayload): string | null;
}

const SLOTS: readonly Slot[] = [
  {
    kind: 'LOGO',
    label: 'Logo',
    hint: 'Shown in the sidebar and on the login page.',
    url: (p) => p.logoUrl,
  },
  {
    kind: 'LOGO_DARK',
    label: 'Logo (dark surfaces)',
    hint: 'Used where the logo sits on a dark background.',
    url: (p) => p.logoDarkUrl,
  },
  {
    kind: 'FAVICON',
    label: 'Favicon',
    hint: 'The browser-tab icon. ICO or a small square PNG works best.',
    url: (p) => p.faviconUrl,
  },
  {
    kind: 'LOGIN_BACKGROUND',
    label: 'Login background',
    hint: 'Behind the sign-in form.',
    url: (p) => p.loginBackgroundUrl,
  },
];

export function AssetUploader({
  payload,
  canWrite,
}: {
  payload: BrandingPayload;
  canWrite: boolean;
}): ReactElement {
  const upload = useUploadAsset();
  const [busyKind, setBusyKind] = useState<AssetKind | null>(null);
  const [errors, setErrors] = useState<Partial<Record<AssetKind, unknown>>>({});

  const pick = async (kind: AssetKind, file: File | undefined): Promise<void> => {
    if (!file) return;
    setBusyKind(kind);
    setErrors((current) => ({ ...current, [kind]: undefined }));
    try {
      await upload.mutateAsync({ kind, file });
    } catch (err) {
      setErrors((current) => ({ ...current, [kind]: err }));
    } finally {
      setBusyKind(null);
    }
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="asset-uploader">
      {SLOTS.map((slot) => {
        const current = slot.url(payload);
        const error = errors[slot.kind];
        return (
          <div
            key={slot.kind}
            className="flex flex-col gap-3 rounded-[var(--radius-md)] border border-line bg-surface p-4"
            data-testid={`asset-slot-${slot.kind}`}
          >
            <div className="flex items-center gap-3">
              {current ? (
                // `alt=""` — the slot label beside it already names it, and a
                // logo announced twice is noise for a screen reader.
                <img
                  src={current}
                  alt=""
                  className="max-h-12 max-w-24 rounded-[var(--radius-sm)] border border-line bg-surface-alt object-contain"
                />
              ) : (
                <span className="flex h-12 w-24 items-center justify-center rounded-[var(--radius-sm)] border border-line bg-surface-alt text-[length:var(--text-xs)] text-muted">
                  None yet
                </span>
              )}
              <div className="min-w-0">
                <p className="text-[length:var(--text-sm)] font-medium text-body">{slot.label}</p>
                <p className="text-[length:var(--text-xs)] text-muted">{slot.hint}</p>
              </div>
            </div>

            {/* Absent, not disabled, for read-only viewers (criterion 15). */}
            {canWrite ? (
              <Field label={`Upload ${slot.label.toLowerCase()}`}>
                {(props) => (
                  <input
                    {...props}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon,image/vnd.microsoft.icon"
                    disabled={busyKind !== null}
                    className="text-[length:var(--text-sm)] text-body file:mr-3 file:rounded-[var(--radius-sm)] file:border file:border-line file:bg-surface-alt file:px-3 file:py-1 file:text-body"
                    onChange={(e) => {
                      void pick(slot.kind, e.target.files?.[0]);
                      // Same file chosen again must re-fire onChange.
                      e.target.value = '';
                    }}
                  />
                )}
              </Field>
            ) : null}

            {busyKind === slot.kind ? (
              <p role="status" className="text-[length:var(--text-xs)] text-muted">
                Uploading…
              </p>
            ) : null}

            {error !== undefined && error !== null ? <ErrorState error={error} /> : null}
          </div>
        );
      })}
    </div>
  );
}
